import type { TeamResponse } from '@/api/generated'

export const GENERAL_TEAM_NAME = 'General'

/** Shared page size for per-team member queries. Higher than the table's
 *  visible row count so client-side ordering/filtering works without an
 *  immediate refetch on every sort change. */
export const TEAM_MEMBERS_PAGE_SIZE = 200

/** Stable react-query key for the per-team members table — shared so any
 *  mutation that moves members (create / disband / edit / remove) can
 *  invalidate every consumer in one call. */
export const TEAM_MEMBERS_TABLE_QUERY_KEY = 'team-members-table'

/** Stable react-query key for `GET /me/active-cost-center` — the response
 *  carries the caller's current team_id + every CC member's
 *  `is_effective_team_admin` flag, which the create-department dialog reads
 *  to decide whether to render the successor-admin field. Any mutation that
 *  could move the caller (or one of their CC peers) between teams must
 *  invalidate this key, otherwise the dialog renders against stale data and
 *  the sole-admin check silently mis-evaluates. */
export const MY_ACTIVE_COST_CENTER_QUERY_KEY = 'my-active-cost-center'

/**
 * The "General" team is the catch-all department every cost center seeds on
 * creation. It cannot be renamed or disbanded — members removed from another
 * department default back to it. Identified by exact name match.
 */
export function isGeneralTeam(team: Pick<TeamResponse, 'name'>): boolean {
  return team.name === GENERAL_TEAM_NAME
}

/**
 * Within an enterprise there is one General team per cost center. For the
 * "where should this member land" dropdown we default to the General team
 * in the SAME cost center as the team we're removing them from. If no
 * same-CC General exists, fall back to any other team in the same CC.
 *
 * Cross-CC moves are intentionally excluded — moving a member between cost
 * centers shifts the billing seat from one Stripe subscription to another,
 * and that shouldn't happen silently as a fallback default. The dialog
 * surfaces the empty case (returns undefined) and prompts the admin to
 * make the cross-CC choice explicitly.
 */
export function pickDefaultDestinationTeamId(
  teams: TeamResponse[],
  sourceTeam: Pick<TeamResponse, 'id' | 'cost_center_id'>
): string | undefined {
  const candidates = teams.filter((t) => t.id !== sourceTeam.id && t.cost_center_id === sourceTeam.cost_center_id)
  const sameCcGeneral = candidates.find((t) => isGeneralTeam(t))
  if (sameCcGeneral) return sameCcGeneral.id
  return candidates[0]?.id
}
